import type { ServerAuthPolicy } from "@t3tools/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { invalidateServerAuthGateState } from "../../environments/primary/auth";
import { signOut } from "../../shared/auth";
import { openAfterAccountChange } from "../../shared/auth/web-address";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

/**
 * Shown in place of the app when a signed-in account lacks this client's
 * platform: on the web address the router refuses a session without `web`
 * (`no_web_access`), and a server refuses a token without its own platform
 * (`desktop` on the local server, `web` on a team server). Signing in again
 * mints the same token, so the page names the missing access and offers
 * another account.
 */
export function PlatformAccessPage({ policy }: { readonly policy: ServerAuthPolicy }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const web = policy === "team-server";

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {web ? t("auth/noAccessToThe_f115av") : t("auth/noAccessToThe_ioeilx")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {web ? (
            <p className="text-muted-foreground text-sm">
              {t("auth/dovodSupportOpensAccess_jrw3he")}
            </p>
          ) : null}
          <Button
            variant="outline"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void signOut().finally(() => {
                invalidateServerAuthGateState();
                openAfterAccountChange("/sign-in", (to) => void navigate({ to, replace: true }));
              });
            }}
          >
            {t("auth/signInWithAnother_7el4g2")}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
